// =============================================================================
// El puente con GdGO! (#326): el alta de transmisiones embebida en el WebView de la app recibe el token de Entra de GdGO!,
// sin login dentro del WebView. Contrato acordado en #326:
// - App → página: GdGO! ejecuta window.gescomAux.recibirToken('<token>') al cargar y cada vez que la página lo pide.
// - Página → app: la página navega a gdgo-bridge://pide-token (al cargar sin token, con menos de cinco minutos de vida o
//   tras un 401) y a gdgo-bridge://transmision-guardada?id=<ID> tras guardar; GdGO! intercepta y cancela la navegación.
// - El token vive solo en memoria: nunca en la URL, en localStorage ni en los registros.
// - Si en 10 s no llega un token válido, la página lo dice y no pide login.
// =============================================================================

export const ESQUEMA = 'gdgo-bridge://';
export const ESPERA_TOKEN_MS = 10_000;
const MARGEN_RENOVACION_MS = 5 * 60_000;
export const SIN_TOKEN = 'No se pudo identificar al asesor. Cierra y vuelve a abrir la pantalla.';

type Navegar = (url: string) => void;

interface EstadoPuente {
  token: string | null;
  caduca: number | null;
  esperas: ((token: string) => void)[];
  navegar: Navegar;
  ahora: () => number;
  renovacion?: ReturnType<typeof setTimeout>;
}

let estado: EstadoPuente | null = null;

declare global {
  interface Window {
    gescomAux?: { recibirToken: (token: string) => void };
  }
}

/** Si la página está embebida en GdGO!: el alta con <c>?embebido=1</c>, la URL del contrato. */
export const esEmbebido = (ubicacion: Pick<Location, 'pathname' | 'search'> = window.location): boolean =>
  /\/transmisiones\/alta\/?$/.test(ubicacion.pathname) && new URLSearchParams(ubicacion.search).get('embebido') === '1';

/**
 * Si se simula el puente: con <c>?simular=1</c> y solo donde el despliegue lo enciende (<c>REACT_APP_ALTA_SIMULAR_PUENTE</c>,
 * que producción no pone: condición 2 de GdGO! en #326).
 */
export const simulacionPermitida = (ubicacion: Pick<Location, 'search'> = window.location): boolean =>
  process.env.REACT_APP_ALTA_SIMULAR_PUENTE === 'true' && new URLSearchParams(ubicacion.search).get('simular') === '1';

/** La caducidad del token (claim <c>exp</c>), en milisegundos; null si no se puede leer. El token no se valida aquí: lo valida la API. */
const caducidad = (token: string): number | null => {
  try {
    const carga = token.split('.')[1];
    const json = JSON.parse(atob(carga.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

/** Instala el puente: <c>window.gescomAux.recibirToken</c> y la navegación a GdGO!. Se llama una vez, al arrancar embebida. */
export const instalarPuente = (opciones: { navegar?: Navegar; ahora?: () => number } = {}): void => {
  estado = {
    token: null,
    caduca: null,
    esperas: [],
    navegar: opciones.navegar ?? ((url) => { window.location.href = url; }),
    ahora: opciones.ahora ?? Date.now,
  };
  window.gescomAux = { recibirToken };
};

/** Quita el puente (pruebas). */
export const desinstalarPuente = (): void => {
  if (estado?.renovacion) clearTimeout(estado.renovacion);
  estado = null;
  delete window.gescomAux;
};

export const puenteInstalado = (): boolean => estado !== null;

/** El token vigente, o null si no hay o ha caducado. */
export const tokenActual = (): string | null => {
  if (!estado?.token) return null;
  return estado.caduca !== null && estado.caduca <= estado.ahora() ? null : estado.token;
};

/** Lo que llama GdGO!: guarda el token en memoria, despierta a quien lo esperaba y programa la renovación. */
const recibirToken = (token: string): void => {
  if (!estado || typeof token !== 'string' || token.length === 0) return;
  estado.token = token;
  estado.caduca = caducidad(token);
  if (estado.renovacion) clearTimeout(estado.renovacion);
  if (estado.caduca !== null) {
    const falta = estado.caduca - estado.ahora() - MARGEN_RENOVACION_MS;
    estado.renovacion = setTimeout(pedirToken, Math.max(falta, 0));
  }
  if (tokenActual()) {
    const esperas = estado.esperas.splice(0);
    esperas.forEach((despertar) => despertar(token));
  }
};

/** Pide a GdGO! un token nuevo. */
export const pedirToken = (): void => estado?.navegar(`${ESQUEMA}pide-token`);

/** Avisa a GdGO! de que se ha guardado una propuesta; GdGO! decide si cierra la página. */
export const avisarGuardada = (id: number): void => estado?.navegar(`${ESQUEMA}transmision-guardada?id=${encodeURIComponent(String(id))}`);

/**
 * El token vigente o, si no hay, se pide a GdGO! y se espera hasta <paramref name="ms"/>; si no llega, rechaza con
 * <see cref="SIN_TOKEN"/>. Con <c>renovar</c>, pide uno nuevo aunque haya uno vigente (tras un 401).
 */
export const esperarToken = (ms = ESPERA_TOKEN_MS, renovar = false): Promise<string> => {
  const actual = renovar ? null : tokenActual();
  if (actual) return Promise.resolve(actual);
  if (!estado) return Promise.reject(new Error(SIN_TOKEN));
  if (renovar) estado.token = null;
  const puente = estado;
  return new Promise<string>((resolver, rechazar) => {
    const plazo = setTimeout(() => {
      puente.esperas = puente.esperas.filter((e) => e !== despertar);
      rechazar(new Error(SIN_TOKEN));
    }, ms);
    const despertar = (token: string) => {
      clearTimeout(plazo);
      resolver(token);
    };
    // Varias llamadas a la vez esperan el mismo token: solo la primera se lo pide a GdGO!.
    const yaPedido = puente.esperas.length > 0;
    puente.esperas.push(despertar);
    if (!yaPedido) pedirToken();
  });
};
